/**
 * Application Configuration
 * 
 * Centralized configuration management that reads from config.json
 * and allows environment variables to override values.
 * All configuration should be accessed through this config file
 * to enable easy updates and maintainability.
 */

import jsonConfig from './config.json';

/**
 * Get OIDC redirect URI from environment variable, config.json, or use default
 */
function getOidcRedirectUri(): string {
  if (process.env.REACT_APP_OIDC_REDIRECT_URI) {
    return process.env.REACT_APP_OIDC_REDIRECT_URI;
  }
  if (jsonConfig.oidc.redirectUri) {
    return jsonConfig.oidc.redirectUri;
  }
  return `${window.location.origin}/oidc/callback`;
}

/**
 * Get tenant-specific environment variable
 */
function getTenantEnvVar(baseKey: string, tenantId: string): string | undefined {
  const tenantEnvKey = tenantId.replace(/[^a-zA-Z0-9]/g, '_').toUpperCase();
  return process.env[`${baseKey}_${tenantEnvKey}`];
}

/**
 * Application configuration object
 * Environment variables override config.json values
 */
export const config = {
  // OIDC Configuration
  oidc: {
    baseUrl: process.env.REACT_APP_OIDC_BASE_URL || jsonConfig.oidc.baseUrl,
    redirectUri: getOidcRedirectUri(),
    getRedirectUri: getOidcRedirectUri, // Function for dynamic access
  },

  // Auth Portal Configuration
  authPortal: {
    url: process.env.REACT_APP_AUTH_PORTAL_URL || jsonConfig.authPortal.url,
  },

  // Tenant Configuration
  tenant: {
    id: process.env.REACT_APP_SYBERKEY_TENANT_ID || jsonConfig.tenant.id,
  },

  // OIDC Client Configuration
  oidcClient: {
    // Get client ID for a tenant (with fallback chain)
    getClientId: (tenantId: string, defaultClientId?: string): string => {
      return (
        getTenantEnvVar('REACT_APP_OIDC_CLIENT_ID', tenantId) ||
        process.env.REACT_APP_OIDC_CLIENT_ID ||
        defaultClientId ||
        `${tenantId}-client-id`
      );
    },

    // Get client secret for a tenant (with fallback chain)
    getClientSecret: (tenantId: string, defaultClientSecret?: string): string => {
      return (
        getTenantEnvVar('REACT_APP_OIDC_CLIENT_SECRET', tenantId) ||
        process.env.REACT_APP_OIDC_CLIENT_SECRET ||
        defaultClientSecret ||
        `${tenantId}-client-secret`
      );
    },
  },

  // Application Metadata
  app: {
    clientName: process.env.REACT_APP_CLIENT_NAME || jsonConfig.app.clientName,
    source: process.env.REACT_APP_SOURCE || jsonConfig.app.source,
  },
};

/**
 * Default OIDC client configurations per tenant
 * These are pre-registered in the backend
 * Loaded from config.json
 */
export const defaultOidcClients: Record<string, { clientId: string; clientSecret: string }> = jsonConfig.defaultOidcClients;

